import React, { useMemo, useRef, useCallback, useState } from 'react';
import { Layout, Select, Button, Modal } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogoutOutlined, HomeOutlined, ShopOutlined, WalletOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { clearUserDetails } from '../../utils/auth';
import { api } from '../../services/api';
import { useVendor } from '../../context/VendorContext';
import { AddCreditModal } from '../Modals';

const { Header, Content } = Layout;
const VENDOR_SELECT_WIDTH = 200;
const VENDOR_LIST_ITEM_HEIGHT = 32;
const VENDOR_LIST_VISIBLE_ITEMS = 5;

const AppLayout = ({ children, disableVendorSelect = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth0();
  const [isCreditModalOpen, setIsCreditModalOpen] = useState(false);
  const {
    selectedVendorId,
    setSelectedVendorId,
    vendors,
    vendorsLoading,
    loadVendors,
    isPublic,
  } = useVendor();

  const vendorOptions = useMemo(() => {
    const options = [
      { value: 'public', label: 'Public' },
      ...vendors.map((v) => ({
        value: v.id,
        label: v.name || `Vendor #${v.id}`,
      })),
    ];

    if (
      selectedVendorId != null &&
      !vendors.some((v) => Number(v.id) === Number(selectedVendorId))
    ) {
      options.push({
        value: selectedVendorId,
        label: `Vendor #${selectedVendorId}`,
      });
    }

    return options;
  }, [vendors, selectedVendorId]);

  const searchTimerRef = useRef(null);
  const handleVendorSearch = useCallback(
    (keyword) => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
      searchTimerRef.current = setTimeout(() => {
        loadVendors(keyword);
      }, 300);
    },
    [loadVendors]
  );

  const handleLogout = () => {
    Modal.confirm({
      title: 'Logout?',
      content: 'Are you sure you want to logout?',
      okText: 'Logout',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        clearUserDetails();

        try {
          await api.logout();
        } catch (error) {
          console.warn('Logout failed, continuing with Auth0 logout:', error);
        }

        logout({
          logoutParams: {
            returnTo: window.location.origin + '/login',
          },
        });
      },
    });
  };

  const handleHome = () => {
    navigate('/roleplays');
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#001529',
          padding: '0 24px',
          gap: 16,
        }}
      >
        <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold', flexShrink: 0 }}>
          Admin
        </div>

        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexShrink: 0,
          }}
        >
          <Select
            showSearch
            allowClear={!isPublic && !disableVendorSelect}
            disabled={disableVendorSelect}
            placeholder="Search vendor…"
            value={selectedVendorId ?? 'public'}
            onChange={(value) => setSelectedVendorId(value == null || value === 'public' ? null : value)}
            onSearch={handleVendorSearch}
            onDropdownVisibleChange={(open) => {
              if (open && !disableVendorSelect) loadVendors('');
            }}
            filterOption={false}
            loading={vendorsLoading}
            options={vendorOptions}
            suffixIcon={<ShopOutlined style={{ color: disableVendorSelect ? 'rgba(0, 0, 0, 0.25)' : 'rgba(30, 41, 59, 0.45)' }} />}
            style={{ width: VENDOR_SELECT_WIDTH }}
            popupMatchSelectWidth={VENDOR_SELECT_WIDTH}
            listHeight={VENDOR_LIST_ITEM_HEIGHT * VENDOR_LIST_VISIBLE_ITEMS}
            aria-label="Vendor scope"
            className="admin-vendor-select"
          />
          {location.pathname !== '/roleplays' && (
            <Button
              type="text"
              icon={<HomeOutlined />}
              onClick={handleHome}
              style={{ color: '#fff' }}
            >
              Roleplays
            </Button>
          )}
          <Button
            type="text"
            icon={<WalletOutlined />}
            onClick={() => setIsCreditModalOpen(true)}
            style={{ color: '#fff' }}
          >
            Add Credits
          </Button>
          <Button
            type="text"
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            style={{ color: '#fff' }}
          >
            Logout
          </Button>
        </div>
      </Header>
      <Content style={{ padding: '24px', background: '#f0f2f5' }}>
        {children}
      </Content>
      <AddCreditModal
        open={isCreditModalOpen}
        onCancel={() => setIsCreditModalOpen(false)}
      />
    </Layout>
  );
};

export default AppLayout;
